import {
  applyPatches,
  enablePatches,
  original,
  produceWithPatches,
  type Patch,
} from "immer";
import {
  MAX_FONT_SIZE,
  MIN_FONT_SIZE,
  cloneStyle,
  nextStyleKey,
  type StyleConfig,
  type StyleKey,
} from "./styles";
import {
  isWord,
  type ArticleMeta,
  type Character,
  type PageElement,
  type Word,
} from "./types";

enablePatches();

/** Everything about one erasure that is worth persisting. */
export interface ErasureDocument {
  /** Stable slug; also the IndexedDB primary key. */
  key: string;
  meta: ArticleMeta;
  page: PageElement[];
  style: StyleConfig;
  /**
   * Which preset `style` was derived from. Persisted so that reopening an erasure
   * resumes the style cycle where the reader left it rather than restarting at the
   * default.
   */
  styleKey: StyleKey;
}

/** One undoable step, stored as forward and inverse immer patches. */
export interface HistoryEntry {
  redo: Patch[];
  undo: Patch[];
  /**
   * Groups the many small edits of a single drag into one undo step. Consecutive
   * recorded actions sharing a transaction id are merged into this entry.
   */
  transaction?: string;
}

export interface AppState {
  /** `null` while the splash screen is showing. */
  document: ErasureDocument | null;
  history: HistoryEntry[];
  /** Index of the most recently applied entry; -1 means nothing to undo. */
  historyIndex: number;
}

/** Bounds history memory. Patches are small, so this is generous. */
export const MAX_HISTORY = 200;

export const initialState: AppState = {
  document: null,
  history: [],
  historyIndex: -1,
};

export type Action =
  | { type: "openDocument"; document: ErasureDocument }
  | { type: "closeDocument" }
  | { type: "setWordsMarked"; ids: string[]; isMarked: boolean; transaction?: string }
  | { type: "toggleWord"; id: string; transaction?: string }
  | { type: "setWordCharacters"; id: string; characters: Character[] }
  | { type: "cycleStyle" }
  | { type: "adjustFontSize"; delta: number }
  | { type: "undo" }
  | { type: "redo" };

/**
 * The page as it was, rather than as a draft.
 *
 * Reading an element through an immer draft creates a proxy for it, so walking
 * `draft.page` drafts every word it passes. Erasing one word on a long article
 * meant tens of thousands of proxies, and a stroke down a full page of text
 * crawled because of it. Everything below therefore *looks* on the original,
 * which is plain data, and reaches into the draft only for the words it writes.
 */
const pageOf = (draft: ErasureDocument): PageElement[] =>
  original(draft.page) ?? draft.page;

/** The draft of one word, located without drafting the page around it. */
const draftWord = (draft: ErasureDocument, index: number): Word | undefined => {
  if (index < 0) return undefined;
  const word = draft.page[index];
  return isWord(word) ? word : undefined;
};

/**
 * Marks or unmarks words, writing the patches by hand rather than through immer.
 *
 * This runs once per pointer event while a reader drags, on a page that can hold
 * thousands of words, and immer's bookkeeping was linear in the page: finalizing
 * the page array walks every element to look for drafts, and copying it takes
 * V8's slow path because immer freezes what it produces. That was half a
 * millisecond a mark on a desktop, before a phone multiplies it. Here the page is
 * copied once and only the words that change are touched. The patches are the
 * same ones immer would write, so undo and redo replay them unchanged.
 */
const markWords = (
  document: ErasureDocument,
  ids: string[],
  isMarked: boolean,
): [ErasureDocument, Patch[], Patch[]] => {
  const wanted = new Set(ids);
  const redo: Patch[] = [];
  const undo: Patch[] = [];
  let page: PageElement[] | undefined;

  for (let index = 0; index < document.page.length && wanted.size > 0; index += 1) {
    const element = document.page[index];
    if (!isWord(element) || !wanted.delete(element.id)) continue;

    let characters = element.characters;
    // A word carved into custom characters keeps its characters in step with
    // the word as a whole, so toggling the word still fully erases it.
    if (element.customCharacters) {
      characters = element.characters.map((character, position) => {
        if (character.isMarked === isMarked) return character;
        const path = ["page", index, "characters", position, "isMarked"];
        redo.push({ op: "replace", path, value: isMarked });
        undo.push({ op: "replace", path, value: character.isMarked });
        return { ...character, isMarked };
      });
    }
    if (element.isMarked !== isMarked) {
      const path = ["page", index, "isMarked"];
      redo.push({ op: "replace", path, value: isMarked });
      undo.push({ op: "replace", path, value: element.isMarked });
    } else if (
      characters === element.characters ||
      characters.every((c, i) => c === element.characters[i])
    ) {
      continue;
    }

    page ??= document.page.slice();
    page[index] = { ...element, isMarked, characters };
  }

  return page ? [{ ...document, page }, redo, undo] : [document, [], []];
};

/**
 * Applies a document mutation. Runs inside immer, so it mutates `draft` freely;
 * the caller turns the mutation into patches.
 */
const mutate = (draft: ErasureDocument, action: Action): void => {
  switch (action.type) {
    case "toggleWord": {
      const word = draftWord(
        draft,
        pageOf(draft).findIndex((e) => e.id === action.id),
      );
      if (!word) return;
      word.isMarked = !word.isMarked;
      if (word.customCharacters) {
        for (const character of word.characters) {
          character.isMarked = word.isMarked;
        }
      }
      return;
    }
    case "setWordCharacters": {
      const word = draftWord(
        draft,
        pageOf(draft).findIndex((e) => e.id === action.id),
      );
      if (!word) return;
      word.characters = action.characters;
      word.customCharacters = true;
      // The word counts as erased only when every character is.
      word.isMarked = action.characters.every((character) => character.isMarked);
      return;
    }
    case "cycleStyle": {
      const key = nextStyleKey(draft.styleKey);
      const fontSize = draft.style.text.global.fontSize;
      draft.styleKey = key;
      draft.style = cloneStyle(key);
      // Font size is a reader preference, not part of the preset.
      draft.style.text.global.fontSize = fontSize;
      return;
    }
    case "adjustFontSize": {
      const next = draft.style.text.global.fontSize + action.delta;
      draft.style.text.global.fontSize = Math.min(
        MAX_FONT_SIZE,
        Math.max(MIN_FONT_SIZE, next),
      );
      return;
    }
    default:
      return;
  }
};

/** Drops history entries ahead of the cursor, then appends or merges the new one. */
const record = (
  state: AppState,
  entry: HistoryEntry,
  transaction: string | undefined,
): Pick<AppState, "history" | "historyIndex"> => {
  const history = state.history.slice(0, state.historyIndex + 1);
  const last = history[history.length - 1];

  // Merge into the open transaction so one drag is one undo step.
  if (transaction && last && last.transaction === transaction) {
    history[history.length - 1] = {
      transaction,
      redo: [...last.redo, ...entry.redo],
      // Inverse patches must be replayed newest-first to unwind correctly.
      undo: [...entry.undo, ...last.undo],
    };
    return { history, historyIndex: history.length - 1 };
  }

  history.push(entry);
  const overflow = Math.max(0, history.length - MAX_HISTORY);
  const trimmed = overflow > 0 ? history.slice(overflow) : history;
  return { history: trimmed, historyIndex: trimmed.length - 1 };
};

export const reducer = (state: AppState, action: Action): AppState => {
  switch (action.type) {
    case "openDocument":
      return { document: action.document, history: [], historyIndex: -1 };

    case "closeDocument":
      return initialState;

    case "undo": {
      if (state.historyIndex < 0 || !state.document) return state;
      const entry = state.history[state.historyIndex];
      return {
        ...state,
        document: applyPatches(state.document, entry.undo),
        historyIndex: state.historyIndex - 1,
      };
    }

    case "redo": {
      if (state.historyIndex >= state.history.length - 1 || !state.document) return state;
      const entry = state.history[state.historyIndex + 1];
      return {
        ...state,
        document: applyPatches(state.document, entry.redo),
        historyIndex: state.historyIndex + 1,
      };
    }

    default: {
      if (!state.document) return state;

      const [document, redo, undo] =
        action.type === "setWordsMarked"
          ? markWords(state.document, action.ids, action.isMarked)
          : produceWithPatches(state.document, (draft) => {
              mutate(draft, action);
            });

      // A no-op edit (re-marking an already-marked word) must not create an
      // undo step the reader has to click through.
      if (redo.length === 0) return state;

      const transaction = "transaction" in action ? action.transaction : undefined;
      return {
        document,
        ...record(state, { redo, undo, transaction }, transaction),
      };
    }
  }
};

export const canUndo = (state: AppState): boolean => state.historyIndex >= 0;

export const canRedo = (state: AppState): boolean =>
  state.historyIndex < state.history.length - 1;
